import { defineComponent, nextTick, PropType } from "vue";
import { Container } from "aurelia-dependency-injection";

import { GroupType, GroupTypeEnum, ParentPermissionEnum, PermissionActionEnum } from "../../../../../enums/enums";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { enumToMap } from "../../../../../common/vue-helpers/modifiers/enum-modifiers";
import { debounce } from "../../../../../common/vue-helpers/debounce";
import {
    formatDateToLocalBasedOnDayMode,
    formatDisplayMessage,
    formatDisplayUsers,
    formatUserInitials
} from "../../../../../common/vue-helpers/modifiers/format-message";
import { markdownToHtml } from "../../../../../common/vue-helpers/modifiers/markdown-to-html-modifier";
import { sanitizeMarkup } from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { getSolutionClassIndex } from "../../../../../common/vue-helpers/solution-color-index";
import {
    IConversationGroup,
    IConversationGroups
} from "../../../../../resources-vue/vue-interfaces/messaging-service/i-family-portal";
import { AnalyticsCategory, AnalyticsService } from "../../../../../services/analytics.service";

export default defineComponent({
    computed: {
        selectedConversationClass() {
            const conversationId = (this.$route.params?.id as string) || this.groupConversations[0].id;
            return (index: number) => {
                return {
                    active: conversationId === this.groupConversations[index].id
                };
            };
        },
        showTermSearchUi() {
            return this.openSearch && this.term.length;
        }
    },

    props: {
        conversationGroupsCount: {
            type: Object as PropType<IConversationGroups>,
            default: null
        },
        groupConversations: {
            type: Array as PropType<IConversationGroup[]>,
            default: null
        },
        selectedGroupType: {
            type: Number as PropType<GroupTypeEnum>,
            default: null
        },
        selectedConversation: {
            type: Object as PropType<IConversationGroup>,
            default: null
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        isLoadingMoreConversations: {
            type: Boolean,
            default: false
        },
        loadingNewGroup: {
            type: Boolean,
            default: false
        },
        pageNumber: {
            type: Number,
            default: null
        },
        openNewConversation: {
            type: Boolean,
            default: false
        }
    },

    emits: ["update:selectedGroupType", "update:selectedConversation", "update:openNewConversation", "termChanged"],

    watch: {
        term: function (newValue, oldValue) {
            if (newValue != oldValue) {
                this.searchTermDebounce();
            }
        },
        loadingNewGroup: function (newValue, oldValue) {
            if (newValue && !oldValue) {
                this.scrollConversationListToTop();
            }
        }
    },

    data() {
        return {
            userOs: "" as string,
            term: "" as string,
            openSearch: false as boolean,
            _permissionManager: null as PermissionManager,
            hasFamilyPortalEditPermission: false as boolean,
            _analyticsService: null,
            searchTermDebounce: null,
            enumToMap,
            GroupTypeEnum: GroupType,
            formatDisplayMessage,
            sanitizeMarkup,
            markdownToHtml,
            formatDateToLocalBasedOnDayMode,
            formatDisplayUsers,
            formatUserInitials,
            getSolutionClassIndex
        };
    },

    created() {
        this.userOs = window.sessionStorage.getItem("userOs");
        this._permissionManager = Container.instance.get(PermissionManager);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this.searchTermDebounce = debounce(this.termChanged, 500);
    },

    mounted() {
        this.checkPermission();
    },

    methods: {
        onGroupSelected(value: GroupTypeEnum) {
            this.scrollConversationListToTop();
            this.$emit("update:selectedGroupType", value);
        },

        termChanged() {
            const trimmed = this.term.trim();
            if (trimmed.length) this.$emit("termChanged", trimmed);
        },

        toggleSearchBox(value: boolean) {
            this.openSearch = value;
            this._analyticsService.logEvent({
                category: AnalyticsCategory.FamilyPortal,
                action: "Search icon"
            });
            if (!this.openSearch) {
                this.term = "";
                this.$emit("termChanged", "");
            }
        },

        navigateToNewConversation() {
            this.$emit("update:openNewConversation", !this.openNewConversation);
            this._analyticsService.logEvent({
                category: AnalyticsCategory.FamilyPortal,
                action: "New Chat"
            });
        },

        cancelNewConversation() {
            console.log("cancelNewConversation");
        },

        handleConversationSelection(conversation: IConversationGroup, navIndex: number) {
            this.$emit("update:selectedConversation", conversation);
            this.$emit("update:openNewConversation", false);
            this._analyticsService.logEvent({
                category: AnalyticsCategory.FamilyPortal,
                action: "New Chat-Send Message"
            });
        },

        async scrollConversationListToTop() {
            const scrollElement = this.$refs.conversationListContainer as HTMLElement;
            if (scrollElement) {
                scrollElement.scrollTop = 0;
                requestAnimationFrame(() => {
                    scrollElement.scrollTop = 0;
                });
            }

            await nextTick();
        },

        checkPermission() {
            const isFPBillingEdit = this._permissionManager.checkPermission(
                ParentPermissionEnum.familyPortalBilling,
                PermissionActionEnum.canEdit
            );
            const isFPClinicalEdit = this._permissionManager.checkPermission(
                ParentPermissionEnum.familyPortalClinical,
                PermissionActionEnum.canEdit
            );
            const isFPSchedulingEdit = this._permissionManager.checkPermission(
                ParentPermissionEnum.familyPortalScheduling,
                PermissionActionEnum.canEdit
            );
            const isFPOthersEdit = this._permissionManager.checkPermission(
                ParentPermissionEnum.familyPortalOthers,
                PermissionActionEnum.canEdit
            );

            this.hasFamilyPortalEditPermission =
                isFPBillingEdit || isFPClinicalEdit || isFPSchedulingEdit || isFPOthersEdit;
        },

        hasGroupPermission(groupType: GroupTypeEnum) {
            return this._permissionManager.checkPermission(
                this.getPermissionEnum(groupType),
                PermissionActionEnum.canView
            );
        },

        getPermissionEnum(groupType: GroupTypeEnum) {
            let permissionEnum: ParentPermissionEnum = null;
            switch (groupType) {
                case GroupTypeEnum.Scheduling:
                    permissionEnum = ParentPermissionEnum.familyPortalScheduling;
                    break;
                case GroupTypeEnum.Billing:
                    permissionEnum = ParentPermissionEnum.familyPortalBilling;
                    break;
                case GroupTypeEnum.Clinical:
                    permissionEnum = ParentPermissionEnum.familyPortalClinical;
                    break;
                default:
                    permissionEnum = ParentPermissionEnum.familyPortalOthers;
                    break;
            }
            return permissionEnum;
        }
    }
});
